import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/router'
import { css } from '@emotion/css'
import { useQuery } from '@tanstack/react-query'
import clsx from 'clsx'
import { atom, useAtom } from 'jotai'
import { CopyIcon, SearchIcon } from 'lucide-react'

import type { GetToolsResponse } from '~/generated/backendProvider'
import { QUERY_KEYS } from '~/constants/keys'
import { UpdateQuery } from '~/dataProcessor/hooks/global/router'
import { Button } from '~/design-system/atom/button'
import { Input } from '~/design-system/atom/input'
import { ToolTipBox } from '~/design-system/atom/tooltip'
import { getTools } from '~/generated/backendProvider'
import { useRouterParsed } from '~/hooks/utils/useRouterParse'
import { useGetConnectorInfo } from '~/pages/connector/[connectorId]'
import { ErrorBoundary } from '~/ui/components/base/errorBoundary'
import { SimpleDropdown } from '~/ui/components/custom/dropdown/simple'
import { Table } from '~/ui/components/custom/Table'
import { useToolkitInfo } from '~/ui/screens/appScreen/queries'
import { useConnectionInfo } from '~/ui/screens/connections'
import { copyText } from '~/utils/dom'
import { Duration } from '~/utils/duration'
import { If } from '~/utils/reactComponent'
import { formatActionName, upperCaseAll } from '~/utils/string'
import { RightSideContent } from '../../containers/base/base'
import { E2EFlowWizard } from '../appScreen/modules/wizard'
import { SearchBox, ShowCustomActionCode } from '../appScreen/toolkitPage'
import { CodeButton } from '../connector'

type ActionItem = GetToolsResponse['items'][number]

export const ActionRenderer = () => {
  const {
    authConfigId,
    connectedAccountId,
    usecase,
    page,
    query,
    router,
    tag,
  } = useRouterParsed()

  const isConnector = Boolean(authConfigId)
  const isConnectedAccount = Boolean(connectedAccountId)

  const { data: connectorInfo } = useGetConnectorInfo(authConfigId)
  const { data: connectionInfo } = useConnectionInfo(connectedAccountId)

  const appName = useMemo(() => {
    if (isConnector) {
      return connectorInfo?.toolkit?.slug
    }
    if (isConnectedAccount) {
      return connectionInfo?.toolkit?.slug
    }
    return undefined
  }, [connectionInfo, connectorInfo, isConnectedAccount, isConnector])

  const {
    data: actionsData = {
      items: [],
    },
    isLoading,
    isSuccess,
  } = useQuery({
    queryKey: QUERY_KEYS.getActionQueryKey(appName || '', query),
    queryFn: () =>
      getTools({
        toolkitSlug: appName ?? undefined,
        search: usecase!,
        limit: '1000',
      }),
    staleTime: Duration({ d: 1 }).toMS(),
    enabled: !!appName,
  })

  const updateQuery = UpdateQuery()

  useEffect(() => {
    if (router.isReady && !page) {
      updateQuery('page', '1')
    }
  }, [router.isReady, updateQuery])

  const headerRenderer = useCallback(() => {
    const tableHeaders = ['Name', 'Slug', 'Description', '']

    return tableHeaders.map((header, index) => (
      <div
        key={index}
        className={clsx(
          'font-gilroy text-[12px] font-[500]',
          index === tableHeaders.length - 1 ? 'flex justify-end pr-4' : '',
        )}
      >
        {header}
      </div>
    ))
  }, [])

  const rowRenderer = useCallback(
    (row: {
      slug: string
      name: string
      description: string
      tags: string[]
    }) => (
      <ActionRow
        actionData={{
          name: row.name,
          slug: row.slug,
          description: row.description || '',
          tags: row.tags.join(','),
        }}
        appName={appName ?? ''}
      />
    ),
    [appName],
  )

  const rows = useMemo(() => {
    if (!actionsData) return []

    let data = actionsData?.items || []

    // TODO (@CryogenicPlanet): this is not there, should be added back if needed
    // const limitedActions =
    //   connectorInfo?.data?.limitedActions?.map((action) =>
    //     action.toUpperCase(),
    //   ) || [];

    // data =
    //   limitedActions && limitedActions.length > 0
    //     ? data.filter((item: ActionItem) =>
    //         limitedActions.includes(item.slug.toUpperCase()),
    //       )
    //     : data;

    if (tag) {
      data = data.filter((item: ActionItem) => item.tags?.includes(tag))
    }

    return data as ActionItem[]
  }, [connectorInfo, actionsData, tag])

  const [, setOpenAction] = useAtom(openActionAtom)

  const { appInfo } = useToolkitInfo(appName ?? '')

  const isCustomApp =
    appInfo?.meta.categories?.some((category) => category.slug === 'custom') ??
    false

  return (
    <div className="h-full w-full">
      <div className="pt-2">
        <ActionNTriggerFilterBar
          isCustomApp={isCustomApp}
          isAction={true}
          actions={actionsData?.items || []}
        />
      </div>
      <Table
        gridCSS={gridBox}
        rows={rows}
        state={isSuccess ? 'success' : isLoading ? 'loading' : 'error'}
        noResultText="No action found"
        headerRenderer={headerRenderer}
        rowRenderer={rowRenderer}
        onRowClick={(row) => {
          setOpenAction(row.name)
        }}
        rowContainerHeight={window.innerHeight - 174}
        rowHeight={50}
      />
    </div>
  )
}

export const ActionNTriggerFilterBar = ({
  isAction = false,
  actions = [],
  isCustomApp = false,
  appName,
}: {
  isAction: boolean
  actions: ActionItem[]
  isCustomApp: boolean
  appName?: string
}) => {
  const router = useRouter()
  const updateQuery = UpdateQuery()

  const { query } = router

  useEffect(() => {
    if (!router.isReady) return

    updateQuery('search', '')
  }, [router.isReady])

  const listOfTags = useMemo(() => {
    if (actions.length === 0) return []

    const tags = []

    for (const item of actions) {
      if (item.tags) {
        for (const tag of item.tags) {
          tags.push(tag)
        }
      }
    }
    return [
      {
        name: 'All',
        value: 'all',
      },
      ...Array.from(new Set(tags)).map((tag) => ({
        name: tag,
        value: tag,
      })),
    ]
  }, [actions])

  const updateQueryFn = UpdateQuery()

  const [open, setShowOpen] = useState(false)

  return (
    <div className="mt-3 flex items-center justify-between text-[13px] text-black-300">
      <div className="flex items-center gap-3">
        <div className="mr-1 min-w-[fit-content] text-[13px]">Filter by: </div>
      </div>
      {open && (
        <ShowCustomActionCode
          setOpen={() => setShowOpen(false)}
          appName={appName || ''}
        />
      )}
      <div
        className={clsx(
          'flex justify-end gap-2',
          listOfTags.length > 1 ? 'w-[550px]' : 'w-[450px]',
        )}
      >
        <If condition={!!appName}>
          <CodeButton
            variant="black"
            text="Add custom action"
            setShowCode={() => setShowOpen(true)}
          />
        </If>
        <If condition={listOfTags.length > 1}>
          <SimpleDropdown
            options={listOfTags}
            onChange={(value) => {
              if (value === 'all') {
                updateQueryFn('tag', '')
              } else {
                updateQueryFn('tag', value as string)
              }
            }}
            value={(query.tag as unknown as string) || ''}
            placeholder="Filter by tag"
          />
        </If>
        {isAction ? (
          <SearchBox isCustomApp={isCustomApp} />
        ) : (
          <Input
            className="min-w-[300px]"
            placeholder="Search by name, app name, connection"
            type="text"
            size={32}
            onChange={(e) => {
              updateQuery('search', e.target.value)
            }}
            defaultValue={query.search || ''}
            icon={<SearchIcon height={14} width={14} color="grey" />}
          />
        )}
      </div>
    </div>
  )
}

export const gridBox = css`
  display: grid;
  grid-template-columns: 300px 100px 1fr 200px;
  gap: 10px;
  width: 100%;
`

const openActionAtom = atom<string | null>(null)

const ActionRow = ({
  actionData,
  appName,
  isLocal = false,
}: {
  actionData: {
    name: string
    slug: string
    description: string
    tags: string
  }
  appName: string
  isLocal?: boolean
}) => {
  const router = useRouter()
  const isAppPage = router.pathname === '/app/[appId]'

  const [isCopied, setIsCopied] = useState(false)
  const [openAction, setOpenAction] = useAtom(openActionAtom)

  const open = openAction === actionData.name

  const { authConfigId, connectedAccountId } = useRouterParsed()

  return (
    <>
      {open && (
        <E2EFlowWizard
          initialData={{
            currentStep: 3,
            first: {
              appName: appName,
            },
            second: {
              authConfigId: authConfigId ?? null,
            },
            third: {
              connectedAccountId: connectedAccountId ?? null,
              actionName: actionData.slug,
            },
          }}
          setOpen={() => {
            setOpenAction(null)
          }}
          isLocal={isLocal}
        />
      )}
      <div
        className="h-[20px] max-w-[full] overflow-hidden overflow-x-auto text-[14px] font-[500] text-black-600"
        title={actionData.name || ''}
      >
        {formatActionName(actionData.name || '')}
      </div>
      <div className="w-fit text-[13px]">
        <ToolTipBox
          align="center"
          side="right"
          content={
            isCopied
              ? '✅ Copied ' + upperCaseAll(actionData.slug)
              : 'Copy ' + upperCaseAll(actionData.slug)
          }
          onOpenChange={(e) => {
            if (!e) {
              setIsCopied(false)
            }
          }}
        >
          <span
            className="flex cursor-pointer items-center gap-2 text-[12px]"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              copyText(upperCaseAll(actionData?.slug)!)
              setIsCopied(true)
            }}
          >
            Slug
            <CopyIcon height={12} width={12} strokeWidth={1.6} />
          </span>
        </ToolTipBox>
      </div>
      <div
        className="word-wrap h-[20px] overflow-hidden text-[13px]"
        title={actionData.description || ''}
      >
        {actionData.description}
      </div>
      <div className="flex w-full items-center justify-end gap-1 pr-3">
        <If condition={!isAppPage}>
          <Button
            size={28}
            variant={'plain'}
            onClick={() => {
              setOpenAction(actionData.slug)
            }}
            className=" mt-[auto] w-[fit-content]  rounded-[10px] border-[.5px] border-[#D5D5D5] !bg-[#FBFBFB] text-[13px] font-[4000] tracking-[.3px] text-black-200"
          >
            <div className="mt-[2px] text-[13px] font-[500]">Run</div>
          </Button>
        </If>
      </div>
    </>
  )
}

ActionRow.whyDidYouRender = {
  logOnDifferentValues: true,
  customName: 'Menu',
}

export default React.memo(function ActionsScreen() {
  return (
    <RightSideContent
      header={
        <div className="w-full">
          <div>
            <span className="min-w-[208px]">Actions</span>
          </div>
        </div>
      }
    >
      <div className="h-full px-4 pb-10 pt-0">
        <ErrorBoundary
          fallback={
            <div>{`Something went wrong. We'll work on getting this fixed.`}</div>
          }
        >
          <ActionRenderer />
        </ErrorBoundary>
      </div>
    </RightSideContent>
  )
})
